import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { useApiGet, useApiSend } from '../../../utils/httpClient';
import {
  getSeekingTransferDetail,
  postSeekingTransferNote,
  updateSeekingTransfer,
  deleteSeekingTransferNote,
  updateSeekingTransferNote,
} from '../../../endpoints/users';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Add, Cancel, Save, Edit, Delete } from '@mui/icons-material';
import ConfirmationDialog from '../../../Components/ConfirmationDialog';
import { roles } from '../../../utils/variables';
import DateFilter from '../../../Components/DateFilter';
import { transferChapterStatuses } from '../../../utils/variables';

export default function SeekingTransferDetail() {
  const { id } = useParams();

  const [newNote, setNewNote] = useState('');
  const [meetingScheduled, setMeetingScheduled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [addNoteId, setAddNoteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState(null);
  const [editingNote, setEditingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [closing, setClosing] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [chapterCompleted, setChapterCompleted] = useState(null);
  const [meetingDate, setMeetingDate] = useState(dayjs());

  const resetNote = () => {
    setNewNote('');
    setMeetingScheduled(false);
    setErrorMessage('');
    setAddNoteId(null);
    setDeleteNoteId(null);
    setIsDeleting(false);
    setEditingNote(false);
    setEditingNoteId(null);
    setClosing(false);
    setIsCompleting(false);
    setChapterCompleted(null);
    setMeetingDate(dayjs());
  };

  const {
    data: transferDetail,
    isLoading,
    refetch,
  } = useApiGet(
    ['seeking-transfer-detail', id],
    getSeekingTransferDetail,
    {
      enabled: !!id,
    },
    { id }
  );

  const { mutate: deleteNote } = useApiSend(
    deleteSeekingTransferNote,
    (data) => {
      refetch();
      resetNote();
    },
    (error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        console.log(error);
      }
    }
  );

  const { mutate: updateNote } = useApiSend(
    updateSeekingTransferNote,
    (data) => {
      refetch();
      resetNote();
    },
    (error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        console.log(error);
      }
    }
  );

  const { mutate: addNote } = useApiSend(
    postSeekingTransferNote,
    (data) => {
      refetch();
      resetNote();
    },
    (error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        console.log(error);
      }
    }
  );

  const { mutate: updateTransfer } = useApiSend(
    updateSeekingTransfer,
    (data) => {
      refetch();
      resetNote();
    },
    (error) => {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        console.log(error);
      }
    }
  );

  const handleCompleteTransfer = () => {
    updateTransfer({
      completed: true,
      id: transferDetail.id,
      local_chapter_id: chapterCompleted,
    });
  };

  const handleCompleteTransferConfirmation = (chapter_id) => {
    setIsCompleting(true);
    setChapterCompleted(chapter_id);
  };

  const handleCompleteTransferCancel = () => {
    setIsCompleting(false);
    setChapterCompleted(null);
  };

  const handleAddNote = () => {
    if (newNote.trim() === '' && noteRequired()) {
      setErrorMessage('Note cannot be empty');
      return;
    }
    setErrorMessage('');
    let chapter_id = null;
    // If the addNoteId is not equal to the transferDetail.id, it means we are adding a note for a specific chapter
    // and the addNoteId would equal one of the chapter IDs
    if (addNoteId && addNoteId !== transferDetail.id) {
      const chapterDetail = transferDetail.local_chapters.find(
        (ch) => ch.id === addNoteId
      );
      if (chapterDetail) {
        chapter_id = addNoteId;
      }
    }
    // If we are editing a note, we need to update it instead of adding a new one
    if (editingNote) {
      updateNote({
        id: transferDetail.id,
        noteId: editingNoteId,
        note: newNote,
        visit_date: meetingScheduled ? meetingDate.format('YYYY-MM-DD') : null,
        chapter_id: chapter_id,
      });
    } else {
      addNote({
        id: transferDetail.id,
        note: newNote,
        visit_date: meetingScheduled ? meetingDate.format('YYYY-MM-DD') : null,
        chapter_id: chapter_id,
        closing: closing,
      });
    }
  };

  const handleDeleteNoteConfirmation = () => {
    deleteNote({
      id: transferDetail.id,
      noteId: deleteNoteId,
    });
  };

  const handleCancelNote = () => {
    resetNote();
  };

  const handleDeleteNote = (noteId) => {
    setDeleteNoteId(noteId);
    setIsDeleting(true);
  };

  const handleEditNote = (note, id) => {
    setNewNote(note.note);
    setAddNoteId(id);
    setErrorMessage('');
    setEditingNote(true);
    setEditingNoteId(note.id);
    if (note.visit_date) {
      setMeetingScheduled(true);
      setMeetingDate(dayjs(note.visit_date));
    }
  };

  const handleCloseTransfer = (id) => {
    setClosing(true);
    setAddNoteId(id);
    setErrorMessage('');
    setNewNote('');
    setMeetingScheduled(false);
    setEditingNote(false);
    setEditingNoteId(null);
  };

  const handleMeetingScheduled = (id) => {
    setMeetingScheduled(true);
    setAddNoteId(id);
    setErrorMessage('');
    setNewNote('');
    setEditingNote(false);
    setEditingNoteId(null);
  };

  const handleAddNoteClick = (id) => {
    setAddNoteId(id);
    setErrorMessage('');
    setNewNote('');
    setMeetingScheduled(false);
    setEditingNote(false);
    setEditingNoteId(null);
  };

  const transferAdmin = () => {
    if (
      [
        '2ca52f70ef7c49d9851d6205128aebf0',
        '92f6b54d6bc64442a27beadc1d32e311',
      ].includes(transferDetail?.status_id)
    ) {
      return false;
    }
    if (localStorage.getItem('userId') === transferDetail?.created_by_id) {
      return true;
    }
    let userRole = localStorage.getItem('role');
    let rolesAllowed = [roles.Admin, roles.Support, roles.SPDOfficer];
    return rolesAllowed.includes(userRole);
  };

  const canEditChapter = () => {
    if (
      [
        '2ca52f70ef7c49d9851d6205128aebf0',
        '92f6b54d6bc64442a27beadc1d32e311',
      ].includes(transferDetail?.status_id)
    ) {
      return false;
    }
    if (localStorage.getItem('userId') === transferDetail?.created_by_id) {
      return true;
    }
    let userRole = localStorage.getItem('role');
    let rolesAllowed = [
      roles.Admin,
      roles.Support,
      roles.SPDOfficer,
      roles.LCOfficer,
    ];
    return rolesAllowed.includes(userRole);
  };

  const canCompleteTransfer = () => {
    if (
      [
        '2ca52f70ef7c49d9851d6205128aebf0',
        '92f6b54d6bc64442a27beadc1d32e311',
      ].includes(transferDetail?.status_id)
    ) {
      return false;
    }
    let userRole = localStorage.getItem('role');
    let rolesAllowed = [
      roles.Admin,
      roles.Support,
      roles.SPDOfficer,
      roles.LCOfficer,
    ];
    return rolesAllowed.includes(userRole);
  };

  const noteRequired = () => {
    if (addNoteId) {
      if (closing || meetingScheduled) {
        return false;
      }
    }
    return true;
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12} textAlign={'center'} paddingTop={2}>
          <Typography variant="body1">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }

  if (!transferDetail && !isLoading) {
    return (
      <Grid container>
        <Grid item xs={12} textAlign={'center'} paddingTop={2}>
          <Typography variant="body1">
            Error loading transfer request info
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={transferDetail?.member_name}
            subheader={`Created on: ${dayjs(transferDetail?.created_at).format('MMM D, YYYY')}`}
            sx={{ p: 1 }}
          />
          <CardContent>
            <Grid item container xs={12}>
              <Grid item xs={12} sm={6} padding>
                <Typography variant="span">
                  <strong>Status:</strong>{' '}
                  {transferDetail?.status_name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} padding>
                <Typography variant="span">
                  <strong>Created By:</strong>{' '}
                  {transferDetail?.created_by || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} padding>
                <Typography variant="span">
                  <strong>Notes:</strong>
                </Typography>
              </Grid>
              {transferDetail.notes.map((note, index) => (
                <Grid container item xs={12} key={index}>
                  <Grid item xs={12} padding>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} padding>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {note.note} -{' '}
                      <b>
                        {note.created_by} -{' '}
                        {dayjs(note.created_at).format('MMM D, YYYY')}
                      </b>
                      {transferAdmin() && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteNote(note.id)}
                          sx={{ float: 'right', padding: 0, marginLeft: 1 }}
                        >
                          <Delete sx={{ fontSize: 18 }} color="primary" />
                        </IconButton>
                      )}
                      {transferAdmin() && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditNote(note, transferDetail.id)
                          }
                          sx={{ float: 'right', padding: 0 }}
                        >
                          <Edit sx={{ fontSize: 18 }} color="blue" />
                        </IconButton>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
              {transferDetail.notes.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ p: 1 }}>
                    No notes available.
                  </Typography>
                </Grid>
              )}
              {addNoteId && addNoteId === transferDetail.id && (
                <Grid container item xs={12}>
                  <Grid item xs={12} padding>
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      rows={2}
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder={
                        noteRequired()
                          ? 'Add a note...'
                          : 'Add a note (optional)'
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} padding>
                    {errorMessage && (
                      <Typography color="error" variant="body2">
                        {errorMessage}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </CardContent>
          {transferAdmin() && (
            <CardActions>
              <Grid container item justifyContent="flex-end">
                {addNoteId && addNoteId === transferDetail.id ? (
                  <Grid item>
                    <Button
                      size="small"
                      color="warning"
                      variant="contained"
                      onClick={handleCancelNote}
                      sx={{ marginRight: 1, fontWeight: 'bold' }}
                    >
                      <Cancel sx={{ pr: 1, fontSize: 18 }} /> Cancel
                    </Button>
                    <Button
                      size="small"
                      color={closing ? 'primary' : 'green'}
                      variant="contained"
                      onClick={handleAddNote}
                      sx={{ fontWeight: 'bold' }}
                    >
                      <Save sx={{ pr: 1, fontSize: 18 }} />{' '}
                      {closing ? 'Close' : 'Save'}
                    </Button>
                  </Grid>
                ) : (
                  <Grid item>
                    <Button
                      size="small"
                      color="green"
                      variant="contained"
                      onClick={() => handleAddNoteClick(transferDetail.id)}
                      sx={{ fontWeight: 'bold', margin: 1 }}
                    >
                      <Add sx={{ pr: 1, fontSize: 18 }} />
                      Add Note
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      onClick={() => handleCloseTransfer(transferDetail.id)}
                      sx={{ fontWeight: 'bold' }}
                    >
                      <Delete sx={{ pr: 1, fontSize: 18 }} />
                      Remove Transfer Request
                    </Button>
                  </Grid>
                )}
              </Grid>
            </CardActions>
          )}
        </Card>
      </Grid>
      {transferDetail?.local_chapters.map((chapter) => (
        <Grid item xs={12} padding key={chapter.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title={chapter.local_chapter_name} sx={{ p: 1 }} />
            <CardContent>
              <Grid item container xs={12}>
                <Grid item xs={12} sm={6} padding>
                  <Typography variant="span">
                    <strong>Status:</strong> {chapter.status_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} padding>
                  <Typography variant="span">
                    <strong>Notes:</strong>
                  </Typography>
                </Grid>
                {chapter.notes.map((note, index) => (
                  <Grid container item xs={12} key={index}>
                    <Grid item xs={12} padding>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} padding>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {note.note}{' '}
                        {note.visit_date &&
                          `(Visit On ${dayjs(note.visit_date).format('MMM D, YYYY')})`}{' '}
                        -{' '}
                        <b>
                          {note.created_by} -{' '}
                          {dayjs(note.created_at).format('MMM D, YYYY')}
                        </b>
                        {transferAdmin() && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteNote(note.id)}
                            sx={{ float: 'right', padding: 0, marginLeft: 1 }}
                          >
                            <Delete sx={{ fontSize: 18 }} color="primary" />
                          </IconButton>
                        )}
                        {transferAdmin() && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditNote(note, chapter.id)}
                            sx={{ float: 'right', padding: 0 }}
                          >
                            <Edit sx={{ fontSize: 18 }} color="blue" />
                          </IconButton>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
                {chapter.notes.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ p: 1 }}>
                      No notes available.
                    </Typography>
                  </Grid>
                )}
                {addNoteId && addNoteId === chapter.id && (
                  <Grid container item xs={12}>
                    <Grid item xs={12} padding>
                      <TextField
                        fullWidth
                        multiline
                        size={'small'}
                        rows={2}
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder={
                          noteRequired()
                            ? 'Add a note...'
                            : 'Add a note (optional)'
                        }
                        variant="outlined"
                      />
                    </Grid>
                    {meetingScheduled && (
                      <Grid item xs={12} sm={6} padding>
                        <DateFilter
                          filterValue={meetingDate}
                          updateFilters={setMeetingDate}
                          label="Meeting Date"
                          disabled={false}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} padding>
                      {errorMessage && (
                        <Typography color="error" variant="body2">
                          {errorMessage}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </CardContent>
            {canEditChapter() && (
              <CardActions>
                <Grid container item justifyContent="flex-end">
                  {addNoteId && addNoteId === chapter.id ? (
                    <Grid item>
                      <Button
                        size="small"
                        color="warning"
                        variant="contained"
                        onClick={handleCancelNote}
                        sx={{ marginRight: 1, fontWeight: 'bold' }}
                      >
                        <Cancel sx={{ pr: 1, fontSize: 18 }} /> Cancel
                      </Button>
                      <Button
                        size="small"
                        color={closing ? 'primary' : 'green'}
                        variant="contained"
                        onClick={handleAddNote}
                        sx={{ fontWeight: 'bold' }}
                      >
                        <Save sx={{ pr: 1, fontSize: 18 }} />
                        {closing ? 'Close' : 'Save'}
                      </Button>
                    </Grid>
                  ) : (
                    <Grid item>
                      <Button
                        size="small"
                        color="green"
                        variant="contained"
                        onClick={() => handleAddNoteClick(chapter.id)}
                        sx={{ fontWeight: 'bold', margin: 1 }}
                      >
                        <Add sx={{ pr: 1, fontSize: 18 }} />
                        Add Note
                      </Button>
                      <Button
                        size="small"
                        color="blue"
                        variant="contained"
                        onClick={() => handleMeetingScheduled(chapter.id)}
                        sx={{ fontWeight: 'bold', margin: 1 }}
                      >
                        <Add sx={{ pr: 1, fontSize: 18 }} />
                        Schedule Visit
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={() => handleCloseTransfer(chapter.id)}
                        sx={{ fontWeight: 'bold', margin: 1 }}
                      >
                        <Delete sx={{ pr: 1, fontSize: 18 }} />
                        Remove Chapter Request
                      </Button>
                    </Grid>
                  )}
                  {canCompleteTransfer() &&
                    !!!addNoteId &&
                    chapter.status_id === transferChapterStatuses.Visit && (
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        sx={{ margin: 1, fontWeight: 'bold' }}
                        onClick={() =>
                          handleCompleteTransferConfirmation(
                            chapter.local_chapter_id
                          )
                        }
                      >
                        <Save sx={{ pr: 1, fontSize: 18 }} /> Transfer Member
                      </Button>
                    )}
                </Grid>
              </CardActions>
            )}
          </Card>
        </Grid>
      ))}
      <ConfirmationDialog
        open={isDeleting}
        handleOk={() => handleDeleteNoteConfirmation()}
        handleCancel={() => resetNote()}
        title="Delete Note"
        message="Are you sure you want to delete this note?"
        confirmText="Delete Note"
        cancelText="Cancel"
      />
      <ConfirmationDialog
        open={isCompleting}
        handleOk={() => handleCompleteTransfer()}
        handleCancel={() => handleCompleteTransferCancel()}
        title="Transfer Member"
        message="This will transfer the member to this chapter and close all open chapter requests? Are you sure?"
        confirmText="Transfer Member"
        cancelText="Cancel"
      />
    </Grid>
  );
}
